import { Clipboard, Palette, TrendUp01 } from "@untitledui/icons";
import type { PropsWithChildren } from "react";
import { useCandidateFilters } from "@/api/candidates";
import { useAdvancedFilters } from "@/api/tiktok-shop";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Label } from "@/components/base/input/label";
import { HorizontalTabs } from "@/components/base/tabs/horizontal-tabs";
import {
  ContentFocusFilter,
  ProductionQualityFilter,
  ToneAndApproachFilter,
  TopSellingCategoriesFilter,
} from "@/components/filters/creator-filters";
import { CreatorListFilter, TagsFilterSection } from "@/components/filters/extra-filters";
import { PercentileSelector, type PercentileValue } from "@/components/filters/percentile-selector";
import { PillField } from "@/components/filters/pill-field";
import {
  FollowersRangeFilter,
  GmvRangeFilter,
  PerformanceSimpleRange,
  SellerRatingRangeFilter,
} from "@/components/filters/range-filters";
import { FilterPanelContainer, FilterPanelHeader, FilterSection, LabelWithTooltip } from "@/components/filters/shared";
import { Accordion } from "@/components/ui/accordion";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { useFilters } from "@/providers/filter-provider";

type CandidateFilterPanelProps = {
  open: boolean;
  close: () => void;
  clearAll?: () => void;
} & PropsWithChildren;

export const CandidateFilterPanel = ({ open, close, clearAll }: CandidateFilterPanelProps) => {
  const { reset } = useFilters();

  const handleClear = () => {
    clearAll?.();
    reset();
  };

  return (
    <FilterPanelContainer open={open}>
      <FilterPanelHeader onClear={handleClear} onClose={close} />
      <div className="flex-1 overflow-y-auto pt-4 px-6 pb-6">
        <CandidateFilters />
      </div>
    </FilterPanelContainer>
  );
};

// =============================================================================
// MAIN CANDIDATE FILTERS COMPONENT
// =============================================================================

// Helper to convert string array to options format
const toOptions = (items?: string[]) => items?.map((item) => ({ id: item, label: item }));

// Helper to convert string array to title-cased options (replaces underscores with spaces)
const toTitleCase = (str: string) =>
  str
    .replace(/_/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const toTitleCaseOptions = (items?: string[]) => items?.map((item) => ({ id: item, label: toTitleCase(item) }));

const CandidateFilters = () => {
  const { form, isActive } = useFilters();
  const { data: filterDefaults, isLoading } = useCandidateFilters();
  const { data: advancedFiltersData } = useAdvancedFilters();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingIndicator size="md" label="Loading filters..." />
      </div>
    );
  }

  return (
    <Form {...form}>
      <div className="flex flex-col gap-6">
        {/* Total GMV - Top Level - Only show when real data exists */}
        {isActive("minGmv") && isActive("maxGmv") && filterDefaults?.maxGmv && (
          <GmvRangeFilter form={form} max={filterDefaults.maxGmv} />
        )}

        {/* Seller Rating - Top Level */}
        {isActive("minRating") && isActive("maxRating") && <SellerRatingRangeFilter form={form} />}

        {/* Collaboration Status - Top Level */}
        {isActive("collaborationStatus") && (
          <FormField
            control={form.control}
            name="collaborationStatus"
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                <Label className="text-sm text-tertiary">Collaboration Status</Label>
                <HorizontalTabs
                  tabs={[
                    { id: "all", label: "All" },
                    { id: "invited", label: "Invited" },
                    { id: "not_invited", label: "Not Invited" },
                  ]}
                  activeTab={field.value ?? "all"}
                  onTabChange={(tabId) => field.onChange(tabId === "all" ? undefined : tabId)}
                />
              </div>
            )}
          />
        )}

        <Accordion type="single" collapsible className="w-full">
          {/* Creator Overview Section */}
          {isActive("minFollowers") && (
            <FilterSection
              id="creator-overview"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <title>Creator Overview</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title="Creator Overview"
            >
              {isActive("minFollowers") && isActive("maxFollowers") && filterDefaults?.maxFollowers && (
                <FollowersRangeFilter form={form} max={filterDefaults.maxFollowers} />
              )}

              {isActive("topSellingCategories") && (
                <FormField
                  control={form.control}
                  name="topSellingCategories"
                  render={({ field }) => (
                    <TopSellingCategoriesFilter field={field} options={toOptions(filterDefaults?.categories)} />
                  )}
                />
              )}

              {isActive("creatorLevel") && (advancedFiltersData?.creatorLevel?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="creatorLevel"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-sm text-tertiary">Creator Level</FormLabel>
                      <PillField
                        value={field.value}
                        options={advancedFiltersData?.creatorLevel ?? []}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("categoryPro") && (advancedFiltersData?.categoryPro?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="categoryPro"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <LabelWithTooltip
                        label="Category Pro"
                        tooltip="Category Pro"
                        description="Creators who regularly post about one product category and have generated most of their sales in that category in the past 60 days."
                      />
                      <PillField
                        value={field.value}
                        options={advancedFiltersData?.categoryPro ?? []}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("creatorLanguage") && (advancedFiltersData?.creatorLanguage?.length ?? 0) > 0 && (
                <FormField
                  control={form.control}
                  name="creatorLanguage"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-2">
                      <FormLabel className="text-sm text-tertiary">Creator Language</FormLabel>
                      <PillField
                        value={field.value}
                        options={advancedFiltersData?.creatorLanguage ?? []}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              )}

              {isActive("minTotalLikes") &&
                filterDefaults?.maxTotalLikes !== undefined &&
                filterDefaults.maxTotalLikes > 0 && (
                  <PerformanceSimpleRange
                    form={form}
                    label="Total Likes"
                    tooltip="Total Likes"
                    description="Cumulative likes across all creator content."
                    minField="minTotalLikes"
                    maxField="maxTotalLikes"
                    min={0}
                    max={filterDefaults.maxTotalLikes}
                    format="followers"
                  />
                )}

              {isActive("minPostingFrequency") &&
                filterDefaults?.maxPostingFrequency &&
                filterDefaults?.maxPostingFrequency > 0 && (
                  <PerformanceSimpleRange
                    form={form}
                    label="Posting Frequency"
                    tooltip="Posting Frequency"
                    description="Average number of videos posted per month."
                    minField="minPostingFrequency"
                    maxField="maxPostingFrequency"
                    min={0}
                    max={filterDefaults.maxPostingFrequency}
                    format="frequency"
                  />
                )}
            </FilterSection>
          )}

          {/* Creator Attributes Section */}
          {isActive("toneAndApproach") && (
            <FilterSection id="creator-attributes" icon={<Palette className="w-5 h-5" />} title="Creator Attributes">
              <FormField
                control={form.control}
                name="toneAndApproach"
                render={({ field }) => (
                  <ToneAndApproachFilter field={field} options={toTitleCaseOptions(filterDefaults?.toneAndApproach)} />
                )}
              />

              {isActive("contentFocus") && (
                <FormField
                  control={form.control}
                  name="contentFocus"
                  render={({ field }) => (
                    <ContentFocusFilter field={field} options={toOptions(filterDefaults?.contentFocus)} />
                  )}
                />
              )}

              {isActive("productionQuality") && (
                <FormField
                  control={form.control}
                  name="productionQuality"
                  render={({ field }) => (
                    <ProductionQualityFilter field={field} options={toOptions(filterDefaults?.productionQuality)} />
                  )}
                />
              )}
            </FilterSection>
          )}

          {/* Audience Section */}
          {(isActive("maleAudiencePercent") || isActive("age")) && (
            <FilterSection
              id="audience"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <title>Audience</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
              title="Audience"
            >
              {isActive("maleAudiencePercent") && (
                <FormField
                  control={form.control}
                  name="maleAudiencePercent"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Male Audience</Label>
                      <PercentileSelector
                        value={(field.value as PercentileValue) ?? "all"}
                        onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                      />
                    </div>
                  )}
                />
              )}

              {isActive("femaleAudiencePercent") && (
                <FormField
                  control={form.control}
                  name="femaleAudiencePercent"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Female Audience</Label>
                      <PercentileSelector
                        value={(field.value as PercentileValue) ?? "all"}
                        onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                      />
                    </div>
                  )}
                />
              )}

              {isActive("age") && (
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <Label className="text-sm text-tertiary">Audience Age Range</Label>
                      <PillField
                        value={field.value}
                        options={["18-24", "25-34", "35-44", "45-54", "55+"]}
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />
              )}
            </FilterSection>
          )}

          {/* Performance Section */}
          {(() => {
            const maxLiveGmv = filterDefaults?.maxLiveGmv;
            const maxVideoGmv = filterDefaults?.maxVideoGmv;
            const hasLiveGmv = maxLiveGmv !== undefined && maxLiveGmv > 0;
            const hasVideoGmv = maxVideoGmv !== undefined && maxVideoGmv > 0;

            return (
              isActive("minLiveGmv") &&
              (hasLiveGmv || hasVideoGmv) && (
                <FilterSection id="performance" icon={<TrendUp01 className="w-5 h-5" />} title="Performance">
                  {hasLiveGmv && (
                    <PerformanceSimpleRange
                      form={form}
                      label="Live GMV"
                      tooltip="Live Stream GMV"
                      description="Total revenue generated from live stream sales."
                      minField="minLiveGmv"
                      maxField="maxLiveGmv"
                      min={0}
                      max={maxLiveGmv}
                      format="currency"
                    />
                  )}
                  {hasVideoGmv && (
                    <PerformanceSimpleRange
                      form={form}
                      label="Video GMV"
                      tooltip="Video Content GMV"
                      description="Total revenue generated from video content sales."
                      minField="minVideoGmv"
                      maxField="maxVideoGmv"
                      min={0}
                      max={maxVideoGmv}
                      format="currency"
                    />
                  )}
                </FilterSection>
              )
            );
          })()}

          <TagsFilterSection />

          {/* Lists Section */}
          {isActive("creatorList") && (
            <FilterSection id="lists" icon={<Clipboard className="w-5 h-5" />} title="Lists">
              <FormField
                control={form.control}
                name="creatorList"
                render={({ field }) => <CreatorListFilter field={field} />}
              />
            </FilterSection>
          )}
        </Accordion>
      </div>
    </Form>
  );
};
